import { supabase } from '@/lib/supabase';
import { uploadToSeaweedFS } from '@/lib/seaweedfs';

const FUNCTIONS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL as string}/functions/v1/content-studio`;
const ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// ─────────────────────────────────────────────────────────────
// Zifek AI Content Studio — accès aux données
// Studio de création de contenu vidéo/multimédia assisté par IA.
// ─────────────────────────────────────────────────────────────

export type ProjectStatus = 'draft' | 'generating' | 'ready' | 'error';
export type SceneStatus = 'pending' | 'generating' | 'ready' | 'error';

export interface ContentProject {
  id: number;
  owner_id: number | null;
  name: string;
  type: string;
  status: ProjectStatus;
  topic: string | null;
  prompt: string | null;
  language: string;
  style: string | null;
  duration_seconds: number;
  aspect_ratio: string;
  narration: boolean;
  target_audience: string | null;
  visual_context: string | null;
  bible: Record<string, unknown>;
  script: string | null;
  script_version: number;
  word_count: number | null;
  estimated_duration: number | null;
  thumbnail_url: string | null;
  ai_provider: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentScene {
  id: number;
  project_id: number;
  number: number;
  script: string | null;
  duration: number;
  visual_prompt: string | null;
  voiceover: string | null;
  music: string | null;
  sound_effects: string | null;
  narrative_prompt: string | null;
  status: SceneStatus;
  active_version_id: number | null;
  locked: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContentSceneVersion {
  id: number;
  scene_id: number;
  version: number;
  video_url: string | null;
  image_url: string | null;
  prompt: string | null;
  status: string;
  request_id: string | null;
  provider: string | null;
  error: string | null;
  media_kind: string | null;
  created_at: string;
}

export interface ContentProjectVersion {
  id: number;
  project_id: number;
  version: number;
  script: string | null;
  label: string | null;
  created_at: string;
}

export interface GeneratedScene {
  number: number;
  script: string;
  duration: number;
  visual_prompt: string;
  voiceover: string;
}

export interface GeneratedStudio {
  title?: string;
  hook?: string;
  script: string;
  wordCount: number;
  estimatedDuration: number;
  scenes: GeneratedScene[];
  provider?: string;
}

export const SCENE_STATUS_META: Record<SceneStatus, { label: string; icon: string; className: string }> = {
  pending: { label: 'En attente', icon: 'ri-time-line', className: 'bg-background-100 text-foreground-500' },
  generating: { label: 'Génération...', icon: 'ri-loader-4-line animate-spin', className: 'bg-primary-100 text-primary-700' },
  ready: { label: 'Prête', icon: 'ri-checkbox-circle-line', className: 'bg-accent-100 text-accent-700' },
  error: { label: 'Erreur', icon: 'ri-error-warning-line', className: 'bg-red-50 text-red-600' },
};

export const PROJECT_TYPE_LABEL: Record<string, string> = {
  video: 'Vidéo', doc: 'Documentaire', ad: 'Publicité', story: 'Histoire',
  social: 'Réseaux sociaux', series: 'Série', audio: 'Audio',
};

/**
 * Métadonnées d'affichage des moteurs IA de génération de texte.
 * Permet d'afficher, en toute transparence, QUEL moteur a réellement produit
 * le script / les scènes (utile pour le diagnostic).
 */
const AI_PROVIDER_META: Record<string, { label: string; icon: string; className: string }> = {
  'xAI': { label: 'xAI Grok', icon: 'ri-flashlight-line', className: 'bg-primary-100 text-primary-700' },
  'OpenRouter': { label: 'OpenRouter', icon: 'ri-route-line', className: 'bg-secondary-100 text-secondary-900' },
  'OpenAI': { label: 'OpenAI', icon: 'ri-openai-line', className: 'bg-accent-100 text-accent-700' },
  'Ollama': { label: 'Ollama (local)', icon: 'ri-server-line', className: 'bg-background-100 text-foreground-700' },
};

/** Renvoie le libellé/icône/couleur d'un moteur IA (repli générique si inconnu). */
export function aiProviderMeta(name: string | null | undefined): { label: string; icon: string; className: string } {
  const key = (name || '').trim();
  if (key && AI_PROVIDER_META[key]) return AI_PROVIDER_META[key];
  if (!key) {
    return { label: 'Non renseigné', icon: 'ri-question-line', className: 'bg-background-100 text-foreground-500' };
  }
  return { label: key, icon: 'ri-cpu-line', className: 'bg-background-100 text-foreground-700' };
}

export const ASPECT_RATIOS: { value: string; label: string; hint: string }[] = [
  { value: '16:9', label: '16:9', hint: 'YouTube / paysage' },
  { value: '9:16', label: '9:16', hint: 'TikTok / Reels / Shorts' },
  { value: '1:1', label: '1:1', hint: 'Instagram / Facebook' },
];

export function formatDuration(seconds: number | null | undefined): string {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}min ${r}s` : `${m}min`;
}

export function timeAgoShort(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `il y a ${d} j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/**
 * Extrait le VRAI message d'erreur renvoyé par la fonction Content Studio.
 * Sans cela, l'utilisateur ne voit que le message générique
 * « Edge Function returned a non-2xx status code ».
 */
async function studioErrorDetail(error: unknown): Promise<string> {
  let detail = error instanceof Error ? error.message : 'Erreur Content Studio';
  try {
    const ctx = (error as { context?: Response } | null)?.context;
    if (ctx && typeof ctx.json === 'function') {
      const parsed = (await ctx.json()) as { error?: string; message?: string };
      detail = parsed?.message || parsed?.error || detail;
    }
  } catch { /* on garde le message par défaut */ }
  if (/non-2xx|timed?\s*out|timeout|504|502/i.test(detail)) {
    return 'Le moteur IA met trop de temps à répondre. Réessayez dans un instant.';
  }
  return detail;
}

/** Forme (partielle) de la réponse renvoyée par la fonction Content Studio. */
interface StudioPayload {
  error?: string;
  message?: string;
  title?: string;
  hook?: string;
  script?: string;
  wordCount?: number;
  estimatedDuration?: number;
  provider?: string;
  scenes?: GeneratedScene[];
  request_id?: string;
  status?: string;
  videoUrl?: string | null;
  duration?: number | null;
  url?: string;
  [key: string]: unknown;
}

/** Appelle le moteur Content Studio avec remontée d'erreur lisible. */
async function invokeStudio(body: Record<string, unknown>): Promise<StudioPayload> {
  const { data, error } = await supabase.functions.invoke('content-studio', { body });
  if (error) throw new Error(await studioErrorDetail(error));
  const payload = (data || {}) as StudioPayload;
  if (payload.error) throw new Error(String(payload.message || payload.error));
  return payload;
}

/** Appelle le moteur de génération (script + scènes). */
export async function generateStudio(params: {
  prompt?: string;
  topic?: string;
  existingScript?: string;
  title?: string;
  language?: string;
  duration?: number;
  style?: string;
  narration?: boolean;
  targetAudience?: string;
  sceneLength?: number;
}): Promise<GeneratedStudio> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'generate', ...params },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return {
    title: data?.title || '',
    hook: data?.hook || '',
    script: data?.script || '',
    wordCount: Number(data?.wordCount) || 0,
    estimatedDuration: Number(data?.estimatedDuration) || 0,
    scenes: Array.isArray(data?.scenes) ? data.scenes : [],
    provider: data?.provider,
  };
}

/**
 * Génère UNIQUEMENT le script (1 appel IA).
 * Séparé du découpage en scènes pour rester sous le délai des Edge Functions
 * (auparavant script + scènes étaient enchaînés dans une seule requête, ce qui
 * provoquait une erreur 504 « upstream server is timing out »).
 */
export async function generateScript(params: {
  prompt?: string;
  topic?: string;
  existingScript?: string;
  title?: string;
  language?: string;
  duration?: number;
  style?: string;
  narration?: boolean;
  targetAudience?: string;
}): Promise<GeneratedStudio> {
  const data = await invokeStudio({ action: 'generate_script', ...params });
  return {
    title: data?.title || '',
    hook: data?.hook || '',
    script: data?.script || '',
    wordCount: Number(data?.wordCount) || 0,
    estimatedDuration: Number(data?.estimatedDuration) || 0,
    scenes: [],
    provider: data?.provider,
  };
}

/**
 * Découpe un script déjà généré en scènes (1 appel IA indépendant).
 */
export async function generateScenes(params: {
  script: string;
  language?: string;
  style?: string;
  sceneLength?: number;
}): Promise<GeneratedScene[]> {
  const data = await invokeStudio({
    action: 'generate_scenes',
    existingScript: params.script,
    language: params.language,
    style: params.style,
    sceneLength: params.sceneLength,
  });
  return Array.isArray(data.scenes) ? data.scenes : [];
}

// ── Projets ──
export async function listProjects(ownerId: number): Promise<ContentProject[]> {
  const { data, error } = await supabase
    .from('content_projects')
    .select('*')
    .eq('owner_id', ownerId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentProject[];
}

export async function getProject(id: number): Promise<ContentProject | null> {
  const { data, error } = await supabase.from('content_projects').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as ContentProject) || null;
}

export async function createProject(params: {
  ownerId: number;
  name: string;
  prompt?: string;
  type?: string;
  language?: string;
  style?: string;
  duration?: number;
  aspectRatio?: string;
  narration?: boolean;
  targetAudience?: string;
  existingScript?: string;
}): Promise<ContentProject> {
  const { data, error } = await supabase
    .from('content_projects')
    .insert({
      owner_id: params.ownerId,
      name: params.name.slice(0, 120),
      prompt: params.prompt || null,
      topic: params.prompt || null,
      type: params.type || 'video',
      status: 'generating',
      language: params.language || 'fr',
      style: params.style || null,
      duration_seconds: params.duration ?? 60,
      aspect_ratio: params.aspectRatio || '16:9',
      narration: params.narration !== false,
      target_audience: params.targetAudience || null,
      script: params.existingScript || null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data as ContentProject;
}

export async function updateProject(id: number, patch: Partial<ContentProject>): Promise<void> {
  const { error } = await supabase
    .from('content_projects')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteProject(id: number): Promise<void> {
  const { error } = await supabase.from('content_projects').delete().eq('id', id);
  if (error) throw error;
}

export async function duplicateProject(project: ContentProject): Promise<ContentProject> {
  const copy = await createProject({
    ownerId: project.owner_id ?? 0,
    name: `${project.name} (copie)`,
    prompt: project.prompt || undefined,
    type: project.type,
    language: project.language,
    style: project.style || undefined,
    duration: project.duration_seconds,
    aspectRatio: project.aspect_ratio,
    narration: project.narration,
    targetAudience: project.target_audience || undefined,
    existingScript: project.script || undefined,
  });
  await updateProject(copy.id, { status: 'ready' });
  return copy;
}

// ── Versions de script ──
export async function listProjectVersions(projectId: number): Promise<ContentProjectVersion[]> {
  const { data, error } = await supabase
    .from('content_project_versions')
    .select('*')
    .eq('project_id', projectId)
    .order('version', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentProjectVersion[];
}

export async function createProjectVersion(projectId: number, script: string, version: number, label?: string): Promise<void> {
  const { error } = await supabase
    .from('content_project_versions')
    .insert({ project_id: projectId, script, version, label: label || null });
  if (error) throw error;
}

// ── Scènes ──
export async function listScenes(projectId: number): Promise<ContentScene[]> {
  const { data, error } = await supabase
    .from('content_scenes')
    .select('*')
    .eq('project_id', projectId)
    .order('number', { ascending: true });
  if (error) throw error;
  return (data || []) as ContentScene[];
}

export async function saveGeneratedScenes(projectId: number, scenes: GeneratedScene[]): Promise<void> {
  // On remplace le découpage : on supprime les anciennes scènes du projet.
  await supabase.from('content_scenes').delete().eq('project_id', projectId);
  if (scenes.length === 0) return;
  const rows = scenes.map((s) => ({
    project_id: projectId,
    number: s.number,
    script: s.script,
    duration: s.duration,
    visual_prompt: s.visual_prompt,
    voiceover: s.voiceover,
    status: 'pending' as const,
  }));
  const { error } = await supabase.from('content_scenes').insert(rows);
  if (error) throw error;
}

/**
 * « Génère » le plan d'une scène : crée une nouvelle version horodatée du plan
 * visuel et la rend active. La version précédente n'est jamais supprimée
 * (historique complet conservé).
 */
export async function generateScenePlan(scene: ContentScene): Promise<void> {
  const versions = await listSceneVersions(scene.id);
  const nextVersion = (versions[0]?.version || 0) + 1;
  const v = await createSceneVersion(scene.id, scene.visual_prompt || '', nextVersion);
  await updateScene(scene.id, { status: 'ready', active_version_id: v?.id ?? null });
}

export async function updateScene(id: number, patch: Partial<ContentScene>): Promise<void> {
  const { error } = await supabase
    .from('content_scenes')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function listSceneVersions(sceneId: number): Promise<ContentSceneVersion[]> {
  const { data, error } = await supabase
    .from('content_scene_versions')
    .select('*')
    .eq('scene_id', sceneId)
    .order('version', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentSceneVersion[];
}

export async function createSceneVersion(sceneId: number, prompt: string, version: number): Promise<ContentSceneVersion | null> {
  const { data, error } = await supabase
    .from('content_scene_versions')
    .insert({ scene_id: sceneId, prompt, version, status: 'ready' })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentSceneVersion) || null;
}

// ─────────────────────────────────────────────────────────────
// PERSONNAGES PERSISTANTS
// Chaque personnage vit au niveau du projet et peut être réutilisé dans
// toutes les scènes (cohérence visuelle entre les scènes).
// ─────────────────────────────────────────────────────────────

export interface ContentCharacter {
  id: number;
  project_id: number;
  name: string;
  description: string | null;
  appearance: string | null;
  reference_image: string | null;
  voice: string | null;
  created_at: string;
}

export async function listCharacters(projectId: number): Promise<ContentCharacter[]> {
  const { data, error } = await supabase
    .from('content_characters')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []) as ContentCharacter[];
}

export async function createCharacter(projectId: number, patch: Partial<ContentCharacter>): Promise<ContentCharacter | null> {
  const { data, error } = await supabase
    .from('content_characters')
    .insert({
      project_id: projectId,
      name: patch.name || 'Personnage',
      description: patch.description ?? null,
      appearance: patch.appearance ?? null,
      reference_image: patch.reference_image ?? null,
      voice: patch.voice ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentCharacter) || null;
}

export async function updateCharacter(id: number, patch: Partial<ContentCharacter>): Promise<void> {
  const { error } = await supabase.from('content_characters').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteCharacter(id: number): Promise<void> {
  const { error } = await supabase.from('content_characters').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// BIBLE DU PROJET (lieux, époque, style, couleurs, règles…)
// Stockée dans content_projects.bible (jsonb) et réutilisée pour garder
// une direction artistique cohérente sur toutes les scènes.
// ─────────────────────────────────────────────────────────────

export interface ProjectBible {
  places?: string;
  era?: string;
  style?: string;
  visualTone?: string;
  colors?: string[];
  rules?: string;
  vocabulary?: string;
}

export const BIBLE_COLOR_SUGGESTIONS = [
  '#C1121F', '#780000', '#D4A373', '#A98467', '#52796F', '#354F52',
  '#2D6A4F', '#1B4332', '#B08968', '#7F5539', '#4A4E69', '#22223B',
  '#E9C46A', '#E76F51', '#F4A261', '#606C38',
];

/** Enregistre la Bible du projet (fusion avec l'existante). */
export async function saveProjectBible(projectId: number, existing: ProjectBible | null, patch: Partial<ProjectBible>): Promise<void> {
  const merged: ProjectBible = { ...(existing || {}), ...patch };
  await updateProject(projectId, { bible: merged as unknown as Record<string, unknown> });
}

// ─────────────────────────────────────────────────────────────
// BIBLIOTHÈQUE MÉDIA
// ─────────────────────────────────────────────────────────────

export type MediaKind = 'image' | 'video' | 'audio' | 'other';

export interface ContentMediaMeta {
  scene_id?: number;
  project_id?: number;
  size?: number;
  mime?: string;
}

export interface ContentMedia {
  id: number;
  owner_id: number | null;
  kind: string;
  url: string;
  name: string;
  meta: ContentMediaMeta | null;
  created_at: string;
}

export function mediaKindFromMime(mime: string): MediaKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'other';
}

export const MEDIA_KIND_META: Record<MediaKind, { label: string; icon: string; className: string }> = {
  image: { label: 'Image', icon: 'ri-image-line', className: 'bg-accent-100 text-accent-700' },
  video: { label: 'Vidéo', icon: 'ri-film-line', className: 'bg-primary-100 text-primary-700' },
  audio: { label: 'Audio', icon: 'ri-music-2-line', className: 'bg-secondary-100 text-secondary-800' },
  other: { label: 'Fichier', icon: 'ri-file-line', className: 'bg-background-100 text-foreground-600' },
};

export async function listMedia(ownerId: number): Promise<ContentMedia[]> {
  const { data, error } = await supabase
    .from('content_media')
    .select('*')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentMedia[];
}

export async function createMedia(params: {
  ownerId: number;
  kind: MediaKind;
  url: string;
  name: string;
  meta?: ContentMediaMeta;
}): Promise<ContentMedia | null> {
  const { data, error } = await supabase
    .from('content_media')
    .insert({
      owner_id: params.ownerId,
      kind: params.kind,
      url: params.url,
      name: params.name.slice(0, 160),
      meta: params.meta || null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentMedia) || null;
}

export async function updateMedia(id: number, patch: { name?: string; meta?: ContentMediaMeta | null }): Promise<void> {
  const { error } = await supabase.from('content_media').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteMedia(id: number): Promise<void> {
  const { error } = await supabase.from('content_media').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// TIMELINE DE MONTAGE
// ─────────────────────────────────────────────────────────────

export interface ContentClip {
  id: number;
  project_id: number;
  scene_id: number | null;
  position: number;
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  created_at: string;
}

export async function listTimelineClips(projectId: number): Promise<ContentClip[]> {
  const { data, error } = await supabase
    .from('content_timeline_clips')
    .select('*')
    .eq('project_id', projectId)
    .order('position', { ascending: true });
  if (error) throw error;
  return (data || []) as ContentClip[];
}

/** Construit / complète la timeline à partir des scènes (une scène = un clip). */
export async function syncTimelineFromScenes(projectId: number, scenes: ContentScene[]): Promise<ContentClip[]> {
  const existing = await listTimelineClips(projectId);
  const usedScenes = new Set(existing.map((c) => c.scene_id).filter((v): v is number => v != null));
  const missing = scenes.filter((s) => !usedScenes.has(s.id));
  if (missing.length > 0) {
    const startPos = existing.length;
    const rows = missing.map((s, i) => ({
      project_id: projectId,
      scene_id: s.id,
      position: startPos + i,
      start_ms: 0,
      end_ms: (s.duration || 5) * 1000,
      duration_ms: (s.duration || 5) * 1000,
    }));
    const { error } = await supabase.from('content_timeline_clips').insert(rows);
    if (error) throw error;
    return listTimelineClips(projectId);
  }
  return existing;
}

/** Réécrit entièrement la timeline depuis les scènes (ordre = numéro de scène). */
export async function rebuildTimeline(projectId: number, scenes: ContentScene[]): Promise<ContentClip[]> {
  await supabase.from('content_timeline_clips').delete().eq('project_id', projectId);
  if (scenes.length === 0) return [];
  const rows = scenes.map((s, i) => ({
    project_id: projectId,
    scene_id: s.id,
    position: i,
    start_ms: 0,
    end_ms: (s.duration || 5) * 1000,
    duration_ms: (s.duration || 5) * 1000,
  }));
  const { error } = await supabase.from('content_timeline_clips').insert(rows);
  if (error) throw error;
  return listTimelineClips(projectId);
}

export async function updateClip(id: number, patch: Partial<ContentClip>): Promise<void> {
  const { error } = await supabase.from('content_timeline_clips').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteClip(id: number): Promise<void> {
  const { error } = await supabase.from('content_timeline_clips').delete().eq('id', id);
  if (error) throw error;
}

/** Ajoute un clip vierge (sans scène) — utile pour un écran-titre par ex. */
export async function addBlankClip(projectId: number, position: number): Promise<void> {
  const { error } = await supabase.from('content_timeline_clips').insert({
    project_id: projectId,
    scene_id: null,
    position,
    start_ms: 0,
    end_ms: 3000,
    duration_ms: 3000,
  });
  if (error) throw error;
}

/** Réordonne les clips : on réécrit la colonne position selon l'ordre fourni. */
export async function persistClipOrder(clips: ContentClip[]): Promise<void> {
  await Promise.all(
    clips.map((c, index) => supabase.from('content_timeline_clips').update({ position: index }).eq('id', c.id)),
  );
}

// ─────────────────────────────────────────────────────────────
// PISTES AUDIO (voix off, musique, effets, ambiance)
// ─────────────────────────────────────────────────────────────

export type AudioKind = 'voiceover' | 'music' | 'sfx' | 'ambience';

export interface ContentAudioTrack {
  id: number;
  project_id: number;
  scene_id: number | null;
  kind: string;
  url: string | null;
  name: string | null;
  volume: number;
  start_ms: number;
  end_ms: number | null;
  created_at: string;
}

export const AUDIO_KIND_META: Record<AudioKind, { label: string; icon: string; className: string }> = {
  voiceover: { label: 'Voix off', icon: 'ri-mic-line', className: 'bg-primary-100 text-primary-700' },
  music: { label: 'Musique', icon: 'ri-music-2-line', className: 'bg-accent-100 text-accent-700' },
  sfx: { label: 'Effets sonores', icon: 'ri-clapperboard-line', className: 'bg-secondary-100 text-secondary-800' },
  ambience: { label: 'Ambiance', icon: 'ri-windy-line', className: 'bg-background-100 text-foreground-600' },
};

export async function listAudioTracks(projectId: number): Promise<ContentAudioTrack[]> {
  const { data, error } = await supabase
    .from('content_audio_tracks')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []) as ContentAudioTrack[];
}

export async function addAudioTrack(projectId: number, patch: Partial<ContentAudioTrack>): Promise<ContentAudioTrack | null> {
  const { data, error } = await supabase
    .from('content_audio_tracks')
    .insert({
      project_id: projectId,
      scene_id: patch.scene_id ?? null,
      kind: patch.kind || 'music',
      url: patch.url ?? null,
      name: patch.name ?? null,
      volume: patch.volume ?? 1,
      start_ms: patch.start_ms ?? 0,
      end_ms: patch.end_ms ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentAudioTrack) || null;
}

export async function updateAudioTrack(id: number, patch: Partial<ContentAudioTrack>): Promise<void> {
  const { error } = await supabase.from('content_audio_tracks').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteAudioTrack(id: number): Promise<void> {
  const { error } = await supabase.from('content_audio_tracks').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// SOUS-TITRES (timecodés, synchronisés sur la narration / l'audio)
// ─────────────────────────────────────────────────────────────

export interface SubtitleCue {
  scene_id: number | null;
  scene_number: number | null;
  start: number;
  end: number;
  text: string;
}

export type SubtitleFormat = 'standard' | 'dynamic' | 'word' | 'social';

export interface SubtitleStyle {
  fontSize?: number;
  position?: 'bottom' | 'center' | 'top';
  color?: string;
  background?: string;
  animation?: 'none' | 'fade' | 'pop' | 'slide';
  font?: string;
}

export interface ContentSubtitle {
  id: number;
  project_id: number;
  scene_id: number | null;
  audio_track_id: number | null;
  format: string;
  style: SubtitleStyle | null;
  content: string | null;
  cues: SubtitleCue[];
  status: string;
  created_at: string;
  updated_at: string | null;
}

export const SUBTITLE_FORMATS: { value: SubtitleFormat; label: string; hint: string }[] = [
  { value: 'standard', label: 'Standard', hint: 'Phrases complètes, lisibles' },
  { value: 'dynamic', label: 'Dynamique', hint: 'Groupes rythmés' },
  { value: 'word', label: 'Mot par mot', hint: 'Karaoké mot à mot' },
  { value: 'social', label: 'Réseaux sociaux', hint: 'Style TikTok / Reels' },
];

export const SUBTITLE_ANIMATIONS: { value: NonNullable<SubtitleStyle['animation']>; label: string }[] = [
  { value: 'none', label: 'Aucune' },
  { value: 'fade', label: 'Fondu' },
  { value: 'pop', label: 'Pop' },
  { value: 'slide', label: 'Glissement' },
];

export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  fontSize: 22, position: 'bottom', color: '#ffffff', background: '#00000099', animation: 'fade', font: 'sans',
};

export async function listSubtitles(projectId: number): Promise<ContentSubtitle[]> {
  const { data, error } = await supabase
    .from('content_subtitles')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []) as ContentSubtitle[];
}

export async function createSubtitle(projectId: number, patch: Partial<ContentSubtitle>): Promise<ContentSubtitle | null> {
  const { data, error } = await supabase
    .from('content_subtitles')
    .insert({
      project_id: projectId,
      scene_id: patch.scene_id ?? null,
      audio_track_id: patch.audio_track_id ?? null,
      format: patch.format || 'standard',
      style: patch.style || DEFAULT_SUBTITLE_STYLE,
      content: patch.content ?? null,
      cues: patch.cues || [],
      status: patch.status || 'draft',
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentSubtitle) || null;
}

export async function updateSubtitle(id: number, patch: Partial<ContentSubtitle>): Promise<void> {
  const { error } = await supabase
    .from('content_subtitles')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteSubtitle(id: number): Promise<void> {
  const { error } = await supabase.from('content_subtitles').delete().eq('id', id);
  if (error) throw error;
}

/** Convertit une liste de cues en fichier SRT. */
export function cuesToSrt(cues: SubtitleCue[]): string {
  const ts = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    const ms = Math.round((s - Math.floor(s)) * 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
  };
  return cues
    .slice()
    .sort((a, b) => a.start - b.start)
    .map((c, i) => `${i + 1}\n${ts(c.start)} --> ${ts(c.end)}\n${c.text}`)
    .join('\n\n');
}

/** Génère des sous-titres timecodés à partir de la narration des scènes. */
export async function generateSubtitles(params: {
  language: string;
  scenes: { scene_id: number; number: number; duration: number; voiceover: string }[];
}): Promise<SubtitleCue[]> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'generate_subtitles', ...params },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return Array.isArray(data?.cues) ? (data.cues as SubtitleCue[]) : [];
}

// ─────────────────────────────────────────────────────────────
// ASSISTANT CRÉATIF IA (branché sur la Bible + les personnages)
// ─────────────────────────────────────────────────────────────

export interface AssistResult {
  script: string | null;
  visual_prompt: string | null;
  narrative_prompt: string | null;
  voiceover: string | null;
  note: string;
  provider?: string;
}

export async function assistScene(params: {
  instruction: string;
  language?: string;
  style?: string;
  bible?: ProjectBible | null;
  characters?: ContentCharacter[];
  scene: { script?: string | null; visual_prompt?: string | null; narrative_prompt?: string | null; voiceover?: string | null; duration?: number };
}): Promise<AssistResult> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: {
      action: 'assist',
      instruction: params.instruction,
      language: params.language || 'fr',
      style: params.style || '',
      bible: params.bible || {},
      characters: (params.characters || []).map((c) => ({
        name: c.name, description: c.description, appearance: c.appearance, voice: c.voice,
      })),
      scene: params.scene,
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return {
    script: data?.script ?? null,
    visual_prompt: data?.visual_prompt ?? null,
    narrative_prompt: data?.narrative_prompt ?? null,
    voiceover: data?.voiceover ?? null,
    note: data?.note || '',
    provider: data?.provider,
  };
}

// ─────────────────────────────────────────────────────────────
// SÉRIES (épisodes liés partageant personnages et style)
// ─────────────────────────────────────────────────────────────

export interface ContentSeries {
  id: number;
  owner_id: number | null;
  name: string;
  description: string | null;
  style: string | null;
  language: string;
  aspect_ratio: string;
  bible: ProjectBible | null;
  character_ids: number[];
  created_at: string;
  updated_at: string;
}

export async function listSeries(ownerId: number): Promise<ContentSeries[]> {
  const { data, error } = await supabase
    .from('content_series')
    .select('*')
    .eq('owner_id', ownerId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentSeries[];
}

export async function createSeries(ownerId: number, patch: Partial<ContentSeries>): Promise<ContentSeries | null> {
  const { data, error } = await supabase
    .from('content_series')
    .insert({
      owner_id: ownerId,
      name: patch.name || 'Nouvelle série',
      description: patch.description ?? null,
      style: patch.style ?? null,
      language: patch.language || 'fr',
      aspect_ratio: patch.aspect_ratio || '16:9',
      bible: patch.bible || null,
      character_ids: patch.character_ids || [],
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentSeries) || null;
}

export async function updateSeries(id: number, patch: Partial<ContentSeries>): Promise<void> {
  const { error } = await supabase
    .from('content_series')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteSeries(id: number): Promise<void> {
  const { error } = await supabase.from('content_series').delete().eq('id', id);
  if (error) throw error;
}

export async function listSeriesEpisodes(seriesId: number): Promise<ContentProject[]> {
  const { data, error } = await supabase
    .from('content_projects')
    .select('*')
    .eq('series_id', seriesId)
    .order('episode_number', { ascending: true });
  if (error) throw error;
  return (data || []) as ContentProject[];
}

/** Crée un nouvel épisode rattaché à une série, en héritant du style/personnages. */
export async function createSeriesEpisode(series: ContentSeries, episodeNumber: number, idea: string): Promise<ContentProject | null> {
  const project = await createProject({
    ownerId: series.owner_id ?? 0,
    name: `${series.name} — Épisode ${episodeNumber}`,
    prompt: idea,
    language: series.language,
    style: series.style || undefined,
    aspectRatio: series.aspect_ratio,
  });
  await updateProject(project.id, {
    series_id: series.id,
    episode_number: episodeNumber,
    status: 'draft',
  });
  return getProject(project.id);
}

// ─────────────────────────────────────────────────────────────
// CALENDRIER DE CONTENU
// ─────────────────────────────────────────────────────────────

export type CalendarStatus = 'planned' | 'in_progress' | 'published';

export interface ContentCalendarEvent {
  id: number;
  owner_id: number | null;
  project_id: number | null;
  series_id: number | null;
  title: string;
  platform: string | null;
  scheduled_date: string | null;
  status: string;
  notes: string | null;
  export_id: number | null;
  media_url: string | null;
  media_kind: string | null;
  thumbnail_url: string | null;
  created_at: string;
}

export const CALENDAR_STATUS_META: Record<CalendarStatus, { label: string; className: string; icon: string }> = {
  planned: { label: 'Prévu', className: 'bg-background-100 text-foreground-600', icon: 'ri-calendar-line' },
  in_progress: { label: 'En cours', className: 'bg-primary-100 text-primary-700', icon: 'ri-loader-4-line' },
  published: { label: 'Publié', className: 'bg-accent-100 text-accent-700', icon: 'ri-checkbox-circle-line' },
};

export const PLATFORMS = ['YouTube', 'YouTube Shorts', 'TikTok', 'Instagram', 'Reels', 'Facebook', 'LinkedIn'];

export async function listCalendarEvents(ownerId: number): Promise<ContentCalendarEvent[]> {
  const { data, error } = await supabase
    .from('content_calendar_events')
    .select('*')
    .eq('owner_id', ownerId)
    .order('scheduled_date', { ascending: true });
  if (error) throw error;
  return (data || []) as ContentCalendarEvent[];
}

export async function createCalendarEvent(ownerId: number, patch: Partial<ContentCalendarEvent>): Promise<ContentCalendarEvent | null> {
  const { data, error } = await supabase
    .from('content_calendar_events')
    .insert({
      owner_id: ownerId,
      project_id: patch.project_id ?? null,
      series_id: patch.series_id ?? null,
      title: patch.title || 'Nouveau contenu',
      platform: patch.platform ?? null,
      scheduled_date: patch.scheduled_date ?? null,
      status: patch.status || 'planned',
      notes: patch.notes ?? null,
      export_id: patch.export_id ?? null,
      media_url: patch.media_url ?? null,
      media_kind: patch.media_kind ?? null,
      thumbnail_url: patch.thumbnail_url ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentCalendarEvent) || null;
}

export async function updateCalendarEvent(id: number, patch: Partial<ContentCalendarEvent>): Promise<void> {
  const { error } = await supabase.from('content_calendar_events').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteCalendarEvent(id: number): Promise<void> {
  const { error } = await supabase.from('content_calendar_events').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// TÉLÉCHARGEMENT D'UN MÉDIA (vidéo / image générée)
// ─────────────────────────────────────────────────────────────

/** Télécharge un média (vidéo/image). Bascule sur un nouvel onglet si le fetch est bloqué. */
export async function downloadMedia(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error('fetch_failed');
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

/** Construit une version de scène enrichie (vidéo, statut, request_id…). */
export interface SceneVersionInsert {
  version: number;
  prompt?: string;
  status?: string;
  request_id?: string | null;
  provider?: string | null;
  media_kind?: string;
}

export async function insertSceneVersion(sceneId: number, input: SceneVersionInsert): Promise<ContentSceneVersion | null> {
  const { data, error } = await supabase
    .from('content_scene_versions')
    .insert({
      scene_id: sceneId,
      version: input.version,
      prompt: input.prompt ?? '',
      status: input.status || 'processing',
      request_id: input.request_id ?? null,
      provider: input.provider ?? null,
      media_kind: input.media_kind ?? 'video',
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentSceneVersion) || null;
}

export async function updateSceneVersion(id: number, patch: Partial<ContentSceneVersion>): Promise<void> {
  const { error } = await supabase
    .from('content_scene_versions')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// MOTEUR VIDÉO xAI (Grok Imagine)
// ─────────────────────────────────────────────────────────────

export interface VideoPollState {
  status: string;
  videoUrl: string | null;
  duration: number | null;
  error: string | null;
}

/** Démarre une génération vidéo (image-to-video si une image est fournie). */
export async function startSceneVideo(params: {
  prompt: string;
  duration?: number;
  aspectRatio?: string;
  resolution?: string;
  image?: string;
}): Promise<{ requestId: string; provider: string }> {
  const data = await invokeStudio({ action: 'generate_scene_video', ...params });
  return { requestId: String(data?.request_id || ''), provider: String(data?.provider || 'xAI') };
}

/** Interroge l'état d'une génération vidéo. */
export async function pollSceneVideo(requestId: string): Promise<VideoPollState> {
  const data = await invokeStudio({ action: 'poll_video', request_id: requestId });
  return {
    status: String(data?.status || 'pending'),
    videoUrl: (data?.videoUrl as string | null) ?? null,
    duration: (data?.duration as number | null) ?? null,
    error: (data?.error as string | null) ?? null,
  };
}

/** Génère des images IA (miniatures, B-roll). */
export async function generateImages(params: {
  prompt: string;
  n?: number;
  aspectRatio?: string;
  resolution?: string;
}): Promise<string[]> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'generate_image', ...params },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return Array.isArray(data?.urls) ? (data.urls as string[]) : [];
}

/** Propose des idées de plans B-roll à partir d'une narration. */
export async function generateBrollIdeas(params: { narration: string; language?: string; style?: string }): Promise<{ label: string; prompt: string }[]> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'broll_ideas', ...params },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return Array.isArray(data?.ideas) ? (data.ideas as { label: string; prompt: string }[]) : [];
}

// ─────────────────────────────────────────────────────────────
// B-ROLL (plans d'illustration générés)
// ─────────────────────────────────────────────────────────────

export interface ContentBroll {
  id: number;
  project_id: number | null;
  scene_id: number | null;
  label: string | null;
  prompt: string | null;
  url: string | null;
  status: string;
  provider: string | null;
  created_at: string;
}

export async function listBroll(projectId: number): Promise<ContentBroll[]> {
  const { data, error } = await supabase
    .from('content_broll')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentBroll[];
}

export async function createBroll(projectId: number, patch: { scene_id?: number | null; label?: string; prompt?: string; url?: string; provider?: string }): Promise<ContentBroll | null> {
  const { data, error } = await supabase
    .from('content_broll')
    .insert({
      project_id: projectId,
      scene_id: patch.scene_id ?? null,
      label: patch.label ?? null,
      prompt: patch.prompt ?? null,
      url: patch.url ?? null,
      provider: patch.provider ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentBroll) || null;
}

export async function deleteBroll(id: number): Promise<void> {
  const { error } = await supabase.from('content_broll').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// MINIATURES (thumbnails)
// ─────────────────────────────────────────────────────────────

export interface ContentThumbnail {
  id: number;
  project_id: number | null;
  prompt: string | null;
  url: string | null;
  selected: boolean;
  provider: string | null;
  created_at: string;
}

export async function listThumbnails(projectId: number): Promise<ContentThumbnail[]> {
  const { data, error } = await supabase
    .from('content_thumbnails')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentThumbnail[];
}

export async function createThumbnail(projectId: number, patch: { prompt?: string; url: string; provider?: string }): Promise<ContentThumbnail | null> {
  const { data, error } = await supabase
    .from('content_thumbnails')
    .insert({ project_id: projectId, prompt: patch.prompt ?? null, url: patch.url, provider: patch.provider ?? null })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentThumbnail) || null;
}

/** Sélectionne une miniature (et la définit comme vignette du projet). */
export async function selectThumbnail(projectId: number, id: number, url: string): Promise<void> {
  await supabase.from('content_thumbnails').update({ selected: false }).eq('project_id', projectId);
  const { error } = await supabase.from('content_thumbnails').update({ selected: true }).eq('id', id);
  if (error) throw error;
  await updateProject(projectId, { thumbnail_url: url });
}

export async function deleteThumbnail(id: number): Promise<void> {
  const { error } = await supabase.from('content_thumbnails').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// REPURPOSING RÉSEAUX SOCIAUX (titres, descriptions, hashtags, shorts)
// ─────────────────────────────────────────────────────────────

export interface RepurposePlatformData {
  title: string;
  description: string;
  hashtags: string[];
}

export interface RepurposeResultData {
  hook: string;
  platforms: Record<string, RepurposePlatformData>;
  shorts: Record<string, number[]>;
  provider?: string;
}

export const REPURPOSE_PLATFORMS: { key: string; label: string; icon: string }[] = [
  { key: 'youtube', label: 'YouTube', icon: 'ri-youtube-line' },
  { key: 'youtube_shorts', label: 'YouTube Shorts', icon: 'ri-play-circle-line' },
  { key: 'tiktok', label: 'TikTok', icon: 'ri-music-line' },
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
  { key: 'facebook', label: 'Facebook', icon: 'ri-facebook-circle-line' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-line' },
];

export async function generateRepurpose(params: {
  title?: string;
  script?: string;
  scenes?: { number: number; script?: string | null }[];
  language?: string;
  style?: string;
}): Promise<RepurposeResultData> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'repurpose', ...params },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return {
    hook: String(data?.hook || ''),
    platforms: data?.platforms || {},
    shorts: data?.shorts || {},
    provider: data?.provider,
  };
}

export interface ContentRepurposeRow {
  id: number;
  project_id: number | null;
  platform: string;
  title: string | null;
  description: string | null;
  hashtags: string[];
  hook: string | null;
  short_seconds: number | null;
  scenes: number[];
  created_at: string;
}

export async function listRepurpose(projectId: number): Promise<ContentRepurposeRow[]> {
  const { data, error } = await supabase
    .from('content_repurpose')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentRepurposeRow[];
}

export async function saveRepurpose(
  projectId: number,
  rows: { platform: string; title?: string; description?: string; hashtags?: string[]; hook?: string; short_seconds?: number | null; scenes?: number[] }[],
): Promise<void> {
  if (rows.length === 0) return;
  await supabase.from('content_repurpose').delete().eq('project_id', projectId);
  const { error } = await supabase.from('content_repurpose').insert(
    rows.map((r) => ({
      project_id: projectId,
      platform: r.platform,
      title: r.title ?? null,
      description: r.description ?? null,
      hashtags: r.hashtags || [],
      hook: r.hook ?? null,
      short_seconds: r.short_seconds ?? null,
      scenes: r.scenes || [],
    })),
  );
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────
// VOIX OFF RÉELLE (synthèse vocale xAI) + MÉDIAS DURABLES
// ─────────────────────────────────────────────────────────────

export const TTS_VOICES: { id: string; label: string; hint: string }[] = [
  { id: 'eve', label: 'Eve', hint: 'Voix claire et neutre' },
  { id: 'ara', label: 'Ara', hint: 'Voix chaleureuse' },
  { id: 'leo', label: 'Leo', hint: 'Voix grave, documentaire' },
  { id: 'rex', label: 'Rex', hint: 'Voix énergique' },
  { id: 'sal', label: 'Sal', hint: 'Voix posée' },
];

/** Génère une vraie voix off (xAI TTS) et renvoie son URL permanente stockée. */
export async function generateVoiceover(params: {
  text: string;
  language?: string;
  voiceId?: string;
}): Promise<{ url: string; provider: string }> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'generate_voice', text: params.text, language: params.language || 'fr', voice_id: params.voiceId || 'eve' },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return { url: String(data?.url || ''), provider: String(data?.provider || 'xAI') };
}

/** Re-télécharge un média xAI temporaire et le ré-héberge pour un lien permanent. */
export async function persistMedia(
  url: string,
  filename: string,
  folder = 'content-studio/media',
  contentType = 'application/octet-stream',
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('content-studio', {
    body: { action: 'persist_media', url, filename, folder, contentType },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data?.message || data.error);
  return String(data?.url || '');
}

/** Récupère les octets d'un média : fetch direct, sinon via le proxy du backend (CORS). */
export async function fetchMediaBytes(url: string): Promise<Uint8Array> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error('direct_failed');
    return new Uint8Array(await res.arrayBuffer());
  } catch {
    const res = await fetch(FUNCTIONS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
      body: JSON.stringify({ action: 'proxy_media', url }),
    });
    if (!res.ok) throw new Error(`Récupération du média impossible (${res.status})`);
    return new Uint8Array(await res.arrayBuffer());
  }
}

/** Téléverse un blob (vidéo finale assemblée…) vers le stockage Zifek. */
export async function uploadBlobToStorage(blob: Blob, filename: string, folder: string): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return uploadToSeaweedFS(bytes, filename, blob.type || 'application/octet-stream', folder);
}

// ─────────────────────────────────────────────────────────────
// EXPORTS (vidéos finales assemblées)
// ─────────────────────────────────────────────────────────────

export interface ContentExport {
  id: number;
  project_id: number | null;
  format: string | null;
  resolution: string | null;
  url: string | null;
  status: string;
  options: Record<string, unknown> | null;
  created_at: string;
}

export async function listExports(projectId: number): Promise<ContentExport[]> {
  const { data, error } = await supabase
    .from('content_exports')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContentExport[];
}

export async function createExport(
  projectId: number,
  patch: { format?: string; resolution?: string; url: string; status?: string; options?: Record<string, unknown> },
): Promise<ContentExport | null> {
  const { data, error } = await supabase
    .from('content_exports')
    .insert({
      project_id: projectId,
      format: patch.format || 'mp4',
      resolution: patch.resolution || null,
      url: patch.url,
      status: patch.status || 'ready',
      options: patch.options || null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return (data as ContentExport) || null;
}

export async function deleteExport(id: number): Promise<void> {
  const { error } = await supabase.from('content_exports').delete().eq('id', id);
  if (error) throw error;
}

/** Dimensions de rendu recommandées selon le format du projet. */
export function renderDimensions(aspectRatio: string): { width: number; height: number } {
  if (aspectRatio === '9:16') return { width: 720, height: 1280 };
  if (aspectRatio === '1:1') return { width: 720, height: 720 };
  if (aspectRatio === '4:5') return { width: 864, height: 1080 };
  return { width: 1280, height: 720 };
}